import {
  Box,
  Button,
  CascaderField,
  Col,
  Form,
  RadioGroupField,
  Row,
  SelectField,
  Text,
  TextInputField,
  theme,
} from '@/components';
import { TagSelectFieldForEdit } from '@/features/tag/components';
import { tagType } from '@/features/tag/models';
import { useHierarchyLabels } from '@/features/tenant-manager/hierarchy-label/_hooks/hierarchy-label';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { UserSelectField } from '@/features/tenant-manager/user/_components/UserSelectField';
import { PlusOutlined } from '@ant-design/icons';
import { type FC, Fragment } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { WorkflowTemplateFormSchema } from '../../models/template';
import {
  WorkflowStageTypeLabelModifier,
  WorkflowTypeLabelModifier,
  workflowStageType,
  workflowType,
} from '../../models/workflow';

export const WORKFLOW_TEMPLATE_FORM_ID = 'workflow-template-form';

const workflowTypeOptions = workflowType.options.map((value) => ({
  value,
  label: WorkflowTypeLabelModifier[value],
}));

const workflowStageTypeOptions = workflowStageType.options.map((value) => ({
  value,
  label: WorkflowStageTypeLabelModifier[value],
}));

export type FormValues = z.infer<typeof WorkflowTemplateFormSchema>;

type Props = {
  onSubmit: (values: FormValues) => void;
};

export const WorkflowTemplateForm: FC<Props> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting, isSubmitSuccessful },
  } = useFormContext<Partial<FormValues>, unknown, FormValues>();

  const { data: hierarchyLabelOptions } = useHierarchyLabels({
    params: { enabledOnly: true },
    select: (data) =>
      data.map((label) => ({
        value: label.id,
        label: label.name,
      })),
  });
  const organizationsOptions = useOrganizationOptionsRecursive();

  const {
    fields: stageFields,
    append: appendStage,
    insert: insertStage,
    remove: removeStage,
  } = useFieldArray<Partial<FormValues>>({
    control,
    name: 'stages',
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      id={WORKFLOW_TEMPLATE_FORM_ID}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <TextInputField
        name="name"
        autocompleteKey="workflow.template.name"
        label={t('workflow.template-name')}
        control={control}
        errors={errors}
        required
      />
      <CascaderField
        name="organizationIds"
        label={t('workflow.organization')}
        control={control}
        errors={errors}
        options={organizationsOptions.data}
        loading={organizationsOptions.isPending}
        required
        multiple
        allowClear
      />
      <TagSelectFieldForEdit
        name="tagIds"
        control={control}
        errors={errors}
        type={tagType.Values.workflow}
        startYear="current"
        endYear="current"
      />
      <Box marginBottom={8}>
        <Text variant="h5" weight="bold" text={t('workflow')} />
      </Box>
      <RadioGroupField
        name="workflowType"
        label={t('workflow-type')}
        control={control}
        options={workflowTypeOptions}
        errors={errors}
      />
      <Form.Item>
        <Row gutter={[0, 8]}>
          {stageFields.map((item, idx) => (
            <Fragment key={item.id}>
              {idx !== 0 && (
                <Col span={24}>
                  <Button
                    type="dashed"
                    onClick={() => {
                      insertStage(idx, {
                        title: null as any,
                        stageType: null as any,
                        approverIds: [],
                        labelId: undefined,
                      });
                    }}
                    block
                    icon={<PlusOutlined aria-label="" />}
                  >
                    {t('add')}
                  </Button>
                </Col>
              )}
              <Col
                style={{
                  padding: 16,
                  backgroundColor: token.colorBgLayout,
                }}
                span={24}
              >
                <Row justify="space-between">
                  <Col>
                    <Text
                      variant="h5"
                      weight="bold"
                      text={t('stage-number', { stageNumber: idx + 1 })}
                    />
                  </Col>
                  <Col>
                    <Button
                      type="link"
                      onClick={() => {
                        removeStage(idx);
                      }}
                    >
                      {t('delete')}
                    </Button>
                  </Col>
                </Row>
                <TextInputField
                  name={`stages.${idx}.title`}
                  autocompleteKey="stage.title"
                  label={t('stage-title')}
                  placeholder={t('placeholder-stage-title')}
                  control={control}
                  errors={errors}
                />

                <SelectField
                  name={`stages.${idx}.labelId`}
                  label={t('label')}
                  control={control}
                  options={hierarchyLabelOptions}
                  useNotInOption
                  errors={errors}
                />

                <RadioGroupField
                  name={`stages.${idx}.stageType`}
                  label={t('stage-type')}
                  control={control}
                  options={workflowStageTypeOptions}
                  errors={errors}
                />

                <UserSelectField
                  mode="multiple"
                  name={`stages.${idx}.approverIds`}
                  label={t('approver')}
                  control={control}
                  errors={errors}
                  organizationFilter="modal"
                  useNotInOption
                />
              </Col>
            </Fragment>
          ))}
          <Col span={24}>
            <Button
              type="dashed"
              onClick={() => {
                appendStage({
                  title: null as any,
                  stageType: null as any,
                  approverIds: [],
                  labelId: undefined,
                });
              }}
              block
              icon={<PlusOutlined aria-label="" />}
            >
              {t('add')}
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </Form>
  );
};
